import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { tickStore, type TickLike } from '../renderer/tick-store.js';
import { getSymbolLabel } from '../state/symbol-labels.js';
import { formatPrice } from '../state/format.js';
import { connectionState } from '../state/connection-state.js';

/**
 * Yatay kayan sembol şeridi — Shadow DOM kullanır (CSS animation izolasyonu).
 *
 * İçerik İKİ KEZ render edilir + translateX(0 → -50%) → DİKİŞSİZ kesintisiz döngü
 * (içerik dar olunca uzun boş bant / tekrar dikişi olmaz) (O12). speed: göreli hız
 * (yüksek = hızlı); süre içerik uzunluğundan bağımsız sabit değil, makul aralıkta.
 */
@customElement('fp-symbol-ticker')
export class FpSymbolTicker extends LitElement {
    @property({ type: Array }) symbols: string[] = [];
    @property({ type: Number }) speed = 60;
    @property({ type: Array }) show: string[] = ['bid'];
    /** Yazı boyutu: auto=1 / large=1.25 / xlarge=1.5 (--fp-ticker-scale). */
    @property({ type: String }) textSize: 'auto' | 'large' | 'xlarge' = 'auto';

    @state() private ticks: Map<string, TickLike> = new Map();
    private unsubscribe?: () => void;
    private prevBids = new Map<string, string>();
    private directions = new Map<string, 'up' | 'down' | 'flat'>();
    private symbolEpoch = new Map<string, number>();

    static styles = css`
        :host {
            display: block;
            width: 100%;
            overflow: hidden;
            background: var(--fp-surface, var(--fp-color-surface));
            color: var(--fp-color-fg);
            padding: 0.4em 0;
            font-family: var(--fp-font-numeric);
            /* Şerit yazısı ekran yüksekliğine oranlı (TV'de okunur) + Yazı boyutu çarpanı. */
            font-size: clamp(16px, calc(3.1vh * var(--fp-ticker-scale, 1)), 72px);
            line-height: 1.2;
        }
        .track {
            display: inline-flex;
            align-items: baseline;
            white-space: nowrap;
            animation-name: scroll;
            animation-timing-function: linear;
            animation-iteration-count: infinite;
            will-change: transform;
        }
        .item {
            display: inline-flex;
            align-items: baseline;
            gap: 0.4em;
        }
        .symbol {
            color: var(--fp-color-fg-muted);
            font-size: 0.82em;
        }
        .price {
            color: var(--fp-color-fg);
            font-weight: 700;
            font-variant-numeric: tabular-nums;
        }
        .price--up {
            color: var(--fp-color-up);
        }
        .price--down {
            color: var(--fp-color-down);
        }
        .arrow {
            font-size: 0.7em;
            margin-left: 0.12em;
        }
        .sep {
            color: var(--fp-color-fg-muted);
            opacity: 0.45;
            margin: 0 1.4em;
        }
        /* İçerik iki kez render → -50% kaydırma dikişsiz döner. */
        @keyframes scroll {
            from { transform: translateX(0); }
            to   { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
            .track { animation: none; }
        }
    `;

    connectedCallback(): void {
        super.connectedCallback();
        this.symbols.forEach((s) => {
            const t = tickStore.get(s);
            if (t) {
                this.ticks.set(s, t);
                if (t.bid !== undefined) this.prevBids.set(s, t.bid);
            }
        });
        this.unsubscribe = tickStore.subscribe((sym, tick) => {
            if (!this.symbols.includes(sym)) return;
            // Yön: bid değişimine göre. Reconnect sonrası ilk tick (epoch değişti) bastırılır (O5).
            const epoch = connectionState.reconnectEpoch;
            const firstThisEpoch = this.symbolEpoch.get(sym) !== epoch;
            this.symbolEpoch.set(sym, epoch);
            const prev = this.prevBids.get(sym);
            if (!firstThisEpoch && prev !== undefined && tick.bid !== undefined && tick.bid !== prev) {
                const a = parseFloat(tick.bid);
                const b = parseFloat(prev);
                if (Number.isFinite(a) && Number.isFinite(b) && a !== b) {
                    this.directions.set(sym, a > b ? 'up' : 'down');
                }
            }
            if (tick.bid !== undefined) this.prevBids.set(sym, tick.bid);
            this.ticks = new Map(this.ticks).set(sym, tick);
        });
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        this.unsubscribe?.();
    }

    private renderItems() {
        const showBid = this.show.includes('bid');
        const showAsk = this.show.includes('ask');
        const showDir = this.show.includes('direction');
        return this.symbols.map((s) => {
            const t = this.ticks.get(s);
            const dir = this.directions.get(s) ?? 'flat';
            // Fiyat metni: ikisi varsa "ALIŞ / SATIŞ"; yoksa hangisi seçiliyse o (varsayılan bid).
            const priceText =
                showBid && showAsk
                    ? `${formatPrice(t?.bid)} / ${formatPrice(t?.ask)}`
                    : formatPrice(showAsk && !showBid ? t?.ask : t?.bid);
            const dirCls = showDir && dir === 'up' ? 'price--up' : showDir && dir === 'down' ? 'price--down' : '';
            const arrow =
                showDir && dir !== 'flat'
                    ? html`<span class="arrow">${dir === 'up' ? '▲' : '▼'}</span>`
                    : '';
            return html`<span class="item">
                    <span class="symbol">${getSymbolLabel(s) ?? s}</span>
                    <span class="price ${dirCls}">${priceText}${arrow}</span>
                </span><span class="sep">•</span>`;
        });
    }

    render() {
        const scale = this.textSize === 'large' ? 1.25 : this.textSize === 'xlarge' ? 1.5 : 1;
        this.style.setProperty('--fp-ticker-scale', String(scale));
        // Süre sembol sayısına göre ölçeklenir (içerik uzunsa daha uzun süre → okunaklı hız).
        const duration = Math.max(12, (this.symbols.length * 60) / Math.max(1, this.speed));
        // İçerik iki kez → -50% kaydırma dikişsiz döner (O12).
        return html`
            <div class="track" style="animation-duration: ${duration}s;">
                ${this.renderItems()}${this.renderItems()}
            </div>
        `;
    }
}
